import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

export default function PropertyValueCalc() {
  const [purchasePrice, setPurchasePrice] = useState(300000);
  const [yearsPurchased, setYearsPurchased] = useState(5);
  const [appreciationRate, setAppreciationRate] = useState(3.5);
  const [improvements, setImprovements] = useState(25000);

  const result = useMemo(() => {
    const appreciated = purchasePrice * Math.pow(1 + appreciationRate / 100, yearsPurchased);
    const estimatedValue = appreciated + improvements * 0.7;
    const equity = estimatedValue - purchasePrice;
    const roi = ((estimatedValue - purchasePrice - improvements) / (purchasePrice + improvements)) * 100;
    return { estimatedValue, equity, roi, appreciated };
  }, [purchasePrice, yearsPurchased, appreciationRate, improvements]);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <Card className="bg-white rounded-3xl border border-stone-200 shadow-sm" data-testid="property-value-calculator">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-xl font-bold text-stone-900">Property Value Estimator</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Purchase Price</Label>
          <Input type="number" value={purchasePrice} onChange={(e) => setPurchasePrice(Number(e.target.value))} className="text-stone-900" data-testid="propval-price" />
          <Slider value={[purchasePrice]} onValueChange={(v) => setPurchasePrice(v[0])} min={50000} max={2000000} step={5000} className="mt-2" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Years Owned</Label>
            <Input type="number" value={yearsPurchased} onChange={(e) => setYearsPurchased(Number(e.target.value))} className="text-stone-900" data-testid="propval-years" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Appreciation (%/yr)</Label>
            <Input type="number" step="0.1" value={appreciationRate} onChange={(e) => setAppreciationRate(Number(e.target.value))} className="text-stone-900" data-testid="propval-appreciation" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Improvements Made</Label>
          <Input type="number" value={improvements} onChange={(e) => setImprovements(Number(e.target.value))} className="text-stone-900" data-testid="propval-improvements" />
        </div>
        <div className="bg-emerald-50 rounded-2xl p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Estimated Value</span>
            <span className="font-outfit font-bold text-emerald-900 text-lg" data-testid="propval-estimated">{fmt(result.estimatedValue)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Total Equity</span>
            <span className="font-semibold text-stone-700">{fmt(result.equity)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Estimated ROI</span>
            <span className="font-semibold text-stone-700">{result.roi.toFixed(1)}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
